import { UIElement } from '../ui-element';
import { element, trustedHTML } from '../../global/template-literals';
import { setAttributes, removeAttributes } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { CmsAttribute } from '../../decorators/cms-attribute.decorator';
import {
    UILinkColor,
    UILinkColors,
    UILinkIconPositions,
    UILinkIconPosition,
    UILinkLayout,
    UILinkLayouts,
    UILinkTarget,
    UILinkTargets,
} from './ui-links.type';
import { UndefinedOr } from '../../global/utils.type';
import { Attributes, CssClasses } from '../ui-element.type';
import { CMS_VALUE_TYPES, CMS_CONTENT_MODES } from '../../cms';
import styles from './ui-link.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UILink
 * @element ui-link
 * @classdesc Represents a class for <code>ui-link</code> element.
 * Wraps or creates a link according to brand-book.
 * Events (like onClick, onChange etc.) and all generic attributes (aria-*, form-*) are being translated
 * straight to the generated link inside ui-link component.
 * @property {string} [label=''] {@attr label} Link text.
 * @property {string} [href="#"] {@attr href} Link url.
 * @property {UILinkTarget} [target] {@attr target} Link target.
 *  {@desc same-window: Open link in same window. Default target by browser.}
 *  {@desc new-window: Open link in new tab/window}
 * @property {UILinkLayout} [layout='basic'] {@attr layout} Link layout.
 *  {@desc basic: Basic link. Link without icon, used within text paragraphs. Default layout}
 *  {@desc primary: Primary link. With left aligned arrow icon, on the left of the label}
 *  {@desc icon: Iconed link. With center aligned icon, on the left or right of the label}
 *  {@desc anchor: Anchored link. For quick navigation within the page, used together with <ui-links>.}
 * @property {string} glyph {@attr glyph} Icon glyph code.
 * @property {UILinkColor} [color='teal'] {@attr color} UILinkColor of icon and text. Primary layout can't be colored.
 *  {@desc teal: Teal blue. Default color for all links.}
 *  {@desc bark-70: Bark brown. For example for navigation links.}
 *  {@desc alert-red: Alert red. For example for remove/delete links.}
 * @property {UILinkIconPosition} [iconPosition='left'] {@attr icon-position} Icon position.
 *  {@desc left: Icon is on the left side of the label. Default position.}
 *  {@desc right: Icon is on the right side of the label}
 * @property {boolean} [isButton=false] {@attr is-button} Whether to use button instead of link tag.
 * @property {HTMLButtonElement} button Read-only. Shortcut to child button element.
 * @property {HTMLLinkElement} link Read-only. Shortcut to child link element.
 * @property {boolean} [disabled=false] {@attr disabled} Makes link disabled.
 * @example
 * <ui-link label="Document link" href="#" glyph="redesign-document" icon-position="left"></ui-link>
 */
@Component({
    tag: 'ui-link',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
    },
})
export class UILink extends UIElement {
    /**
     * @type {UILinkLayouts}
     * @readonly
     */
    static readonly UILinkLayout: UILinkLayouts = {
        Basic: 'basic',
        Primary: 'primary',
        Icon: 'icon',
        Anchor: 'anchor',
        Navigation: 'navigation',
    } as const;

    /**
     * @type {UILinkIconPositions}
     * @readonly
     */
    static readonly UILinkIconPosition: UILinkIconPositions = {
        Left: 'left',
        Right: 'right',
    } as const;

    /**
     * @type {UILinkTargets}
     * @readonly
     */
    static readonly UILinkTarget: UILinkTargets = {
        SameWindow: '_self',
        NewWindow: '_blank',
    } as const;

    /**
     * @type {UILinkColors}
     * @readonly
     */
    static readonly UILinkColor: UILinkColors = {
        Teal: 'teal',
        Bark70: 'bark-70',
        AlertRed: 'alert-red',
    } as const;

    @Attribute(String) label: string = '';
    @CmsAttribute({ valueType: CMS_VALUE_TYPES.Url })
    @Attribute(String)
    href: string = '#';
    @Attribute(String) target?: UILinkTarget;
    @Attribute(String) layout: UILinkLayout = UILink.UILinkLayout.Basic;
    @Attribute(String) glyph?: string;
    @Attribute(String) color: UILinkColor = UILink.UILinkColor.Teal;
    @Attribute(String) iconPosition: UILinkIconPosition =
        UILink.UILinkIconPosition.Left;
    @Attribute(Boolean) isButton: boolean = false;

    static get observedAttributes(): Array<string> {
        return ['label', 'href', 'title'];
    }

    /**
     * Shortcut to the painted inner link or button.
     * @private
     */
    private get interactiveElement():
        | HTMLAnchorElement
        | HTMLButtonElement
        | null {
        return this.querySelector<HTMLAnchorElement | HTMLButtonElement>(
            ':scope > a, :scope > button'
        );
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string,
        newValue: string | null
    ): void {
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'label':
                this.updateLabel(newValue);
                break;
            case 'href':
                this.updateHref(newValue);
                break;
            case 'title':
                this.forwardAttrToInteractive(name, newValue);
                break;
            default:
                break;
        }
    }

    /**
     * Updates the painted label when the host label attribute changes.
     * @param {string | null} newValue
     * @private
     */
    private updateLabel(newValue: string | null): void {
        const interactive = this.interactiveElement;
        if (!interactive) {
            return;
        }
        const labelSpan = interactive.querySelector('span');
        if (newValue) {
            if (labelSpan) {
                labelSpan.textContent = newValue;
            } else {
                const span = document.createElement('span');
                span.textContent = newValue;
                const caption = interactive.querySelector('ui-caption');
                if (caption) {
                    caption.append(span);
                } else {
                    interactive.append(span);
                }
            }
        } else if (labelSpan) {
            labelSpan.remove();
        }
    }

    /**
     * Updates the painted href when the host href attribute changes.
     * @param {string | null} newValue
     * @private
     */
    private updateHref(newValue: string | null): void {
        const interactive = this.interactiveElement;
        if (interactive instanceof HTMLAnchorElement) {
            interactive.setAttribute('href', newValue || '#');
        }
    }

    /**
     * Forwards a host attribute that was moved onto the inner control at first render.
     * @param {string} name
     * @param {string | null} newValue
     * @private
     */
    private forwardAttrToInteractive(
        name: string,
        newValue: string | null
    ): void {
        const interactive = this.interactiveElement;
        if (!interactive || newValue === null) {
            return;
        }
        interactive.setAttribute(name, newValue);
        this.removeAttribute(name);
    }

    /**
     * Constructs a link element to render.
     * @private
     * @returns {HTMLElement | undefined}
     */
    private getLinkElement(): UndefinedOr<HTMLElement> {
        // Label
        const innerHTML = this.innerHTML.trim();
        const label = innerHTML ? trustedHTML`${innerHTML}` : this.label;
        const labelElement = label && element`<span>${label}</span>`;

        // Link contents based on layout
        switch (this.layout) {
            case UILink.UILinkLayout.Basic:
            case UILink.UILinkLayout.Primary:
                return this.isButton
                    ? element`<button type="button" class="ui-link__button">${labelElement}</button>`
                    : element`<a href="${this.href}" target="${this.target || null}">${labelElement}</a>`;
            case UILink.UILinkLayout.Icon:
                const isLeftIcon =
                    this.glyph &&
                    this.iconPosition === UILink.UILinkIconPosition.Left;
                const isRightIcon =
                    this.glyph &&
                    this.iconPosition === UILink.UILinkIconPosition.Right;
                const iconElement = element`<ui-icon glyph="${this.glyph}"></ui-icon>`;
                const labelWithIconElement = element`<ui-caption>
                    ${isLeftIcon && iconElement}
                    ${labelElement}
                    ${isRightIcon && iconElement}
                </ui-caption>`;
                return this.isButton
                    ? element`<button type="button" class="ui-link__button-with-icon">${labelWithIconElement}</button>`
                    : element`<a href="${this.href}" target="${this.target || null}">${labelWithIconElement}</a>`;
            case UILink.UILinkLayout.Anchor:
            case UILink.UILinkLayout.Navigation:
                const glyph =
                    this.layout === UILink.UILinkLayout.Anchor
                        ? 'redesign-navigate-down'
                        : 'redesign-navigate-forward';
                const labelWithAnchorElement = element`<ui-caption>
                    ${labelElement}
                    <ui-icon glyph="${glyph}"></ui-icon>
                </ui-caption>`;
                return this.isButton
                    ? element`<button>${labelWithAnchorElement}</button>`
                    : element`<a href="${this.href}" target="${this.target || null}">${labelWithAnchorElement}</a>`;
            default:
                return;
        }
    }

    /**
     * Get additional classes and attributes to set for link or button such as 'disabled' or 'onclick'.
     * @private
     * @returns {Array<string[] | {string: string}>}
     */
    private getClassesAndAttributes(): {
        genericClasses: CssClasses;
        genericAttributes: Attributes<string>;
    } {
        const excludedAttributes = [
            'label',
            'icon-position',
            'color',
            'target',
            'href',
            'is-button',
            'glyph',
            'layout',
            'state',
            'class',
        ];
        const genericLinkAttributes: Attributes<string> = {};
        const cssClasses: CssClasses = [];
        Array.from(this.attributes)
            .filter((attribute) => {
                if (attribute.name === 'class') {
                    attribute.value.split(' ').forEach((cssClass) => {
                        cssClass && cssClasses.push(cssClass);
                    });
                }
                return !excludedAttributes.includes(attribute.name);
            })
            .forEach((attribute) => {
                genericLinkAttributes[attribute.name] = attribute.value;
            });
        return {
            genericClasses: cssClasses,
            genericAttributes: genericLinkAttributes,
        };
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const { genericClasses, genericAttributes } =
            this.getClassesAndAttributes();

        const linkElement = this.getLinkElement();

        if (linkElement && genericClasses.length > 0) {
            genericClasses.forEach((cssClass) => {
                linkElement.classList.add(cssClass);
            });
        }

        this.innerHTML = '';
        if (linkElement) {
            this.append(linkElement);
        }

        if (linkElement && genericAttributes) {
            setAttributes(linkElement, genericAttributes);
            removeAttributes(this, Object.keys(genericAttributes));
        }
    }
}
